import React, { useContext, useEffect, useState } from 'react'
import { Form, Modal, Row, Switch, Col, Input, Button, Tooltip, AutoComplete } from 'antd'
import { HistoryOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons'
import _ from 'lodash'
import { isObject, safeObjectToString } from '@/common'
import FormLayout from 'components/FormLayout'
import ManageSuppliers, { ManageSuppliersContext } from 'components/ManageSuppliers'

export default function FlightSuppliers() {
    return (
        <ManageSuppliers product="flight" resource="flight-supplier">
            <InternalFlightSuppliers />
        </ManageSuppliers>
    )
}

function InternalFlightSuppliers() {
    const { canEdit, onRegisterColumns, onOpenEditForm, onSelectSupplier, onCopySupplier, dispatchEvent } = useContext(
        ManageSuppliersContext
    )

    const onShowChangeMockBookingConfirm = record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.mock_booking ? 'off' : 'on'} mock booking`,
            onOk: async () => {
                dispatchEvent('mock_booking', record)
            }
        })
    }

    const onShowChangeStatusConfirm = record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.active ? 'off' : 'on'} ${record.name}`,
            onOk: async () => {
                dispatchEvent('status', record)
            }
        })
    }

    const columns = [
        {
            key: 'code',
            dataIndex: 'code',
            title: 'Code',
            defaultSortOder: 'ascend'
        },
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            width: '20%',
            ellipsis: true
        },
        {
            key: 'source_id',
            dataIndex: 'source_id',
            title: 'Source Id'
        },
        {
            key: 'airline_code',
            dataIndex: 'airline_code',
            title: 'Airline Code',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => a.airline_code.localeCompare(b.airline_code)
        },
        {
            key: 'mock_booking',
            dataIndex: 'mock_booking',
            title: 'Mock Booking',
            render: (_, record) => (
                <Switch
                    disabled={!canEdit}
                    checked={record.mock_booking}
                    onClick={() => onShowChangeMockBookingConfirm(record)}
                />
            )
        },
        {
            key: 'status',
            dataIndex: 'active',
            title: 'Active',
            render: (_, record) => (
                <Switch disabled={!canEdit} checked={record.active} onClick={() => onShowChangeStatusConfirm(record)} />
            )
        },
        {
            key: 'action',
            title: 'Action',
            render: (_, record) => {
                return (
                    <Row gutter={[8, 8]}>
                        <Col>
                            <Tooltip title="Edit">
                                <Button
                                    ghost
                                    type="primary"
                                    icon={<EditOutlined />}
                                    disabled={!canEdit}
                                    onClick={() => onOpenEditForm(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col>
                            <Tooltip title="Show Log">
                                <Button
                                    type="dashed"
                                    icon={<HistoryOutlined />}
                                    onClick={() => onSelectSupplier(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col>
                            <Tooltip title="Copy">
                                <Button icon={<CopyOutlined />} onClick={() => onCopySupplier(record)} />
                            </Tooltip>
                        </Col>
                    </Row>
                )
            }
        }
    ]

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => onRegisterColumns(columns), [])

    return (
        <React.Fragment>
            <FlightSupplierForm />
        </React.Fragment>
    )
}

function FlightSupplierForm() {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const { suppliers, formState, onHideForm, onSave, onReload } = useContext(ManageSuppliersContext)

    const fields = [
        'source_id',
        'name',
        'code',
        'airline_code',
        'url',
        'username',
        'passwd',
        'role_code',
        'channel',
        'agency_code',
        'default_currency',
        'custom_config',
        'client_id',
        'client_secret'
    ]

    useEffect(() => {
        form.resetFields()

        if (formState.action === 'edit') {
            const fieldsValue = _.pick(formState.payload, fields)
            fieldsValue.custom_config = safeObjectToString(fieldsValue.custom_config)
            form.setFieldsValue(fieldsValue)
        }
    }, [formState])

    const onSubmit = () => {
        form.validateFields().then(fieldsValue => {
            setLoading(true)

            onSave(fieldsValue)
                .then(() => {
                    setLoading(false)
                    onHideForm()
                    onReload()
                })
                .catch(() => {
                    setLoading(false)
                })
        })
    }

    return (
        <FormLayout
            type="supplier"
            action={formState.action}
            open={formState.action === 'create' || formState.action === 'edit'}
            loading={loading}
            onCloseForm={onHideForm}
            onSubmit={onSubmit}>
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Source Id"
                            name="source_id"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'Source Id is required'
                                }
                            ]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item
                            label="Code"
                            name="code"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'Supplier code is required'
                                }
                            ]}>
                            <AutoComplete
                                showSearch
                                disabled={formState.action === 'edit'}
                                filterOption={(input, option) => RegExp('.*' + input, 'gi').test(option.label)}
                                options={_.chain(suppliers)
                                    .unionBy(s => s.code)
                                    .map(s => ({
                                        label: s.code,
                                        value: s.code
                                    }))
                                    .value()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Name"
                            name="name"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'Name is required'
                                }
                            ]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Airline Code" name="airline_code" hasFeedback>
                            <Input minLength={2} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item label="Username" name="username">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Password" name="passwd">
                            <Input.Password />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={24}>
                        <Form.Item
                            label="Url"
                            name="url"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'URL is required'
                                }
                            ]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item label="Role Code" name="role_code">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Channel" name="channel">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item label="Agency Code" name="agency_code">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item
                            label="Default Currency"
                            name="default_currency"
                            hasFeedback
                            rules={[
                                {
                                    max: 3,
                                    message: 'Currency code must be 3 alphabet characters'
                                }
                            ]}>
                            <Input maxLength={3} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item label="Client Id" name="client_id">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Client Secret" name="client_secret">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Custom Config"
                            name="custom_config"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value && !isObject(value)) {
                                            return Promise.reject('Invalid object')
                                        }

                                        return Promise.resolve()
                                    }
                                }
                            ]}>
                            <Input.TextArea
                                row={6}
                                onChange={e =>
                                    form.setFieldsValue({
                                        custom_config: !e.target.value ? null : e.target.value
                                    })
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FormLayout>
    )
}
